import React, { useEffect, useState } from "react";
import api, { eur } from "../lib/api";
import { Card } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Badge } from "./ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "./ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Tag, RotateCcw } from "lucide-react";
import { toast } from "sonner";

const WHOLE_ENSEIGNE = "__enseigne__"; // valeur de select : tarif par defaut de toute l'enseigne (pas un depot precis)

export const PricingManager = () => {
  const [enseignes, setEnseignes] = useState([]);
  const [enseigne, setEnseigne] = useState("");
  const [allClients, setAllClients] = useState([]);
  const [depotId, setDepotId] = useState(WHOLE_ENSEIGNE);
  const [products, setProducts] = useState([]);
  const [ensOverrides, setEnsOverrides] = useState({}); // product_id -> prix par defaut de l'enseigne (reference)
  const [overrides, setOverrides] = useState({}); // product_id -> prix au niveau actuellement affiche (enseigne ou depot)
  const [drafts, setDrafts] = useState({}); // product_id -> saisie en cours

  useEffect(() => {
    api.get("/enseignes").then((r) => {
      setEnseignes(r.data);
      if (r.data[0]) setEnseigne(r.data[0].name);
    }).catch(() => {});
    api.get("/products").then((r) => setProducts(r.data)).catch(() => {});
    api.get("/clients").then((r) => setAllClients(r.data)).catch(() => {});
  }, []);

  const depots = allClients.filter((c) => c.enseigne === enseigne);

  const loadEnsOverrides = (ens) => {
    if (!ens) return Promise.resolve({});
    return api.get("/price-overrides", { params: { enseigne: ens } }).then((r) => {
      const map = {};
      r.data.forEach((o) => { map[o.product_id] = o.price; });
      setEnsOverrides(map);
      return map;
    }).catch(() => ({}));
  };

  useEffect(() => {
    setDepotId(WHOLE_ENSEIGNE);
    loadEnsOverrides(enseigne).then((map) => { setOverrides(map); setDrafts({}); });
  }, [enseigne]);

  useEffect(() => {
    setDrafts({});
    if (depotId === WHOLE_ENSEIGNE) {
      setOverrides(ensOverrides);
      return;
    }
    api.get("/price-overrides", { params: { client_id: depotId } }).then((r) => {
      const map = {};
      r.data.forEach((o) => { map[o.product_id] = o.price; });
      setOverrides(map);
    }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [depotId]);

  const isDepotScope = depotId !== WHOLE_ENSEIGNE;
  const draftVal = (p) => (drafts[p.id] !== undefined ? drafts[p.id] : (overrides[p.id] !== undefined ? String(overrides[p.id]) : ""));
  const fallbackPrice = (p) => (isDepotScope && ensOverrides[p.id] !== undefined ? ensOverrides[p.id] : p.price);
  const depotName = depots.find((d) => d.id === depotId)?.name;

  const save = async (p) => {
    const raw = draftVal(p).trim();
    const price = raw === "" ? null : parseFloat(raw);
    if (raw !== "" && (Number.isNaN(price) || price < 0)) { toast.error("Prix invalide"); return; }
    await api.put("/price-overrides", { enseigne, client_id: isDepotScope ? depotId : undefined, product_id: p.id, price });
    const scopeLabel = isDepotScope ? depotName : `toute l'enseigne ${enseigne}`;
    toast.success(price === null ? `${p.name} : tarif par défaut rétabli (${scopeLabel})` : `${p.name} : ${eur(price)} pour ${scopeLabel}`);
    if (isDepotScope) {
      const r = await api.get("/price-overrides", { params: { client_id: depotId } });
      const map = {}; r.data.forEach((o) => { map[o.product_id] = o.price; }); setOverrides(map);
    } else {
      const map = await loadEnsOverrides(enseigne);
      setOverrides(map);
    }
    setDrafts((d) => { const { [p.id]: _, ...rest } = d; return rest; });
  };

  const reset = (p) => { setDrafts((d) => ({ ...d, [p.id]: "" })); save(p); };

  return (
    <Card className="mt-6 overflow-hidden" data-testid="pricing-manager">
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-border p-4">
        <div className="flex items-center gap-2">
          <Tag className="h-5 w-5 text-primary" />
          <div>
            <h3 className="font-display font-bold">Tarifs négociés</h3>
            <p className="text-xs text-muted-foreground">
              Par enseigne (tarif par défaut) ou par dépôt précis (prioritaire sur le tarif de l'enseigne). Vide = tarif catalogue.
            </p>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Label className="text-xs uppercase tracking-wider text-muted-foreground">Enseigne</Label>
          <Select value={enseigne} onValueChange={setEnseigne}>
            <SelectTrigger className="w-52" data-testid="pricing-enseigne-select"><SelectValue placeholder="Choisir…" /></SelectTrigger>
            <SelectContent>
              {enseignes.map((e) => <SelectItem key={e.name} value={e.name}>{e.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Label className="text-xs uppercase tracking-wider text-muted-foreground">Portée</Label>
          <Select value={depotId} onValueChange={setDepotId}>
            <SelectTrigger className="w-64" data-testid="pricing-depot-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value={WHOLE_ENSEIGNE}>Toute l'enseigne (tarif par défaut)</SelectItem>
              {depots.map((d) => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>
      <div className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Produit</TableHead>
              <TableHead>Prix catalogue</TableHead>
              {isDepotScope && <TableHead>Tarif de l'enseigne</TableHead>}
              <TableHead className="w-48">{isDepotScope ? `Prix négocié — ${depotName}` : "Prix négocié (€) — enseigne"}</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {products.map((p) => {
              const hasOverride = overrides[p.id] !== undefined;
              return (
                <TableRow key={p.id} data-testid={`pricing-row-${p.id}`}>
                  <TableCell className="font-medium">
                    {p.name}
                    {hasOverride && (
                      <Badge variant="outline" className="ml-2 border-primary/40 bg-primary/10 text-primary">
                        {isDepotScope ? "négocié pour ce dépôt" : "négocié"}
                      </Badge>
                    )}
                  </TableCell>
                  <TableCell className="tabular text-muted-foreground">{eur(p.price)}</TableCell>
                  {isDepotScope && (
                    <TableCell className="tabular text-muted-foreground">
                      {eur(fallbackPrice(p))}
                      {ensOverrides[p.id] === undefined && <span className="ml-1 text-[10px]">(catalogue)</span>}
                    </TableCell>
                  )}
                  <TableCell>
                    <Input
                      type="number"
                      step="0.01"
                      className="h-9 w-36 tabular"
                      data-testid={`pricing-input-${p.id}`}
                      placeholder={`défaut (${eur(fallbackPrice(p))})`}
                      value={draftVal(p)}
                      onChange={(e) => setDrafts((d) => ({ ...d, [p.id]: e.target.value }))}
                    />
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-2">
                      <Button size="sm" onClick={() => save(p)} data-testid={`pricing-save-${p.id}`}>Enregistrer</Button>
                      {hasOverride && (
                        <Button size="sm" variant="ghost" className="text-secondary" onClick={() => reset(p)} data-testid={`pricing-reset-${p.id}`}>
                          <RotateCcw className="mr-1 h-3.5 w-3.5" /> Défaut
                        </Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              );
            })}
            {products.length === 0 && (
              <TableRow><TableCell colSpan={isDepotScope ? 5 : 4} className="py-8 text-center text-muted-foreground">Aucun produit.</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </div>
    </Card>
  );
};
